import { UserCheck, MapPin, BarChart3 } from "lucide-react";

const C = {
  surface:  "#111827",
  border:   "#1e293b",
  muted:    "#6b7280",
  subtle:   "#9ca3af",
  text:     "#e2e8f0",
  green:    "#00ff87",
  greenDim: "rgba(0,255,135,0.08)",
  yellow:   "#fbbf24",
  yellowDim:"rgba(251,191,36,0.08)",
  red:      "#ef4444",
  redDim:   "rgba(239,68,68,0.08)",
  cyan:     "#38bdf8",
  cyanDim:  "rgba(56,189,248,0.08)",
};

/**
 * RefereePanel — renders referee name, venue info and (when available)
 * aggregated historical stats for the referee: average yellow/red cards
 * and fouls per match, computed from the last N fixtures they officiated.
 */
export function RefereePanel({ referee, venue, stats, className = "" }) {
  const hasReferee = referee && referee.trim() !== "";
  const venueName = venue?.name;
  const venueCity = venue?.city;
  const hasAnyStat = stats
    && (stats.matches_analysed || 0) > 0
    && (stats.avg_yellow_cards != null
        || stats.avg_red_cards != null
        || stats.avg_fouls != null);
  const showHistoryBlock = hasReferee;

  if (!hasReferee && !venueName) return null;

  return (
    <div
      data-testid="referee-panel"
      className={className}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <h3
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: C.muted,
          margin: 0,
        }}
      >
        Info Campo
      </h3>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(venueName || venueCity) && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={iconBoxStyle(C.greenDim, "rgba(0,255,135,0.2)")}>
              <MapPin size={14} color={C.green} />
            </div>
            <div>
              {venueName && (
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.4 }}>
                  {venueName}
                </div>
              )}
              {venueCity && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {venueCity}
                </div>
              )}
            </div>
          </div>
        )}

        {hasReferee && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={iconBoxStyle(C.yellowDim, "rgba(251,191,36,0.2)")}>
              <UserCheck size={14} color={C.yellow} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 1 }}>Arbitro</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {referee}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Historical referee stats */}
      {showHistoryBlock && (
        <div
          data-testid="referee-stats-block"
          style={{
            marginTop: 4,
            paddingTop: 12,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.15em",
              textTransform: "uppercase", color: C.cyan,
              marginBottom: 10,
              textShadow: `0 0 8px rgba(56,189,248,0.45)`,
            }}
          >
            <BarChart3 size={11} />
            {hasAnyStat
              ? `Storico · ultimi ${stats.matches_analysed} match`
              : "Storico arbitro"}
          </div>

          {hasAnyStat ? (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 8,
              }}>
                <StatTile
                  label="Cartellini gialli"
                  value={stats.avg_yellow_cards}
                  suffix="/m"
                  color={C.yellow}
                  glow="rgba(251,191,36,0.55)"
                  testid="ref-avg-yellow"
                />
                <StatTile
                  label="Cartellini rossi"
                  value={stats.avg_red_cards}
                  suffix="/m"
                  color={C.red}
                  glow="rgba(239,68,68,0.55)"
                  decimals={2}
                  testid="ref-avg-red"
                />
                <StatTile
                  label="Falli"
                  value={stats.avg_fouls}
                  suffix="/m"
                  color={C.green}
                  glow="rgba(0,255,135,0.55)"
                  testid="ref-avg-fouls"
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  fontSize: 10,
                  color: C.muted,
                  fontStyle: "italic",
                }}
              >
                Basato su {stats.matches_with_cards || 0} match con dati cartellini
                {" · "}
                {stats.matches_with_fouls || 0} con dati falli
              </div>
            </>
          ) : (
            <div
              data-testid="referee-stats-empty"
              style={{
                fontSize: 11,
                color: C.muted,
                fontStyle: "italic",
                lineHeight: 1.45,
              }}
            >
              Storico non ancora disponibile per questo arbitro · i dati
              verranno calcolati man mano che le sue partite vengono
              analizzate nell'app.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, suffix, color, glow, decimals = 1, testid }) {
  const hasValue = value != null && !Number.isNaN(value);
  const shown = hasValue
    ? Number(value).toFixed(decimals).replace(/\.0+$/, (m) => decimals === 1 ? ".0" : m)
    : "—";
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
        border: `1px solid ${hasValue ? color + "40" : C.border}`,
        borderRadius: 8,
        padding: "10px 12px",
        textAlign: "center",
        boxShadow: hasValue ? `0 0 14px ${glow}` : "none",
      }}
    >
      <div
        style={{
          fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: C.muted,
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", justifyContent: "center", gap: 3 }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 20,
            fontWeight: 800,
            color: hasValue ? color : C.muted,
            textShadow: hasValue ? `0 0 12px ${glow}` : "none",
            lineHeight: 1,
          }}
        >
          {shown}
        </span>
        {hasValue && (
          <span style={{ fontSize: 9, color: C.subtle, fontWeight: 600 }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function iconBoxStyle(bg, borderColor) {
  return {
    width: 32, height: 32, borderRadius: 8,
    background: bg, border: `1px solid ${borderColor}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  };
}
